import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { BookOpen, Download, LogOut, MonitorX } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from './AuthContext'
import { usePermissions } from './PermissionsContext'
import { DOCS_ROOT } from './docsLinks'
import { forgetRememberedBrowsers } from '../api/client'
import { useToast } from '../design/Toast'
import { describeFailure } from '../features/connect/failures'

function initialsOf(name: string | null | undefined): string {
  if (!name) return '?'
  const letters = name
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part[0])
    .join('')
  return letters.slice(0, 2).toUpperCase() || '?'
}

/**
 * Identity, the documentation, the Support desktop app, and sign-out. Built on Radix DropdownMenu, which brings focus and keyboard
 * handling. The Support app is here rather than in the navigation: it is each technician's own tool, not a page
 * about the fleet.
 */
export function UserMenu() {
  const navigate = useNavigate()
  const { logout, email, displayName } = useAuth()
  const { user } = usePermissions()
  const toast = useToast()

  // Every browser remembered after giving the authenticator code, this one included, asks again at the next sign-in
  // (Plans/sign-in-remembered-browsers.md): what to do after signing in on a computer that wasn't yours.
  async function forgetBrowsers() {
    if (!user) return
    try {
      await forgetRememberedBrowsers(user.id)
      toast.success('Every browser will ask for your authenticator code at your next sign-in.')
    } catch (error) {
      toast.error(describeFailure(error, 'Your remembered browsers could not be forgotten.'))
    }
  }

  // The profile from /api/me is authoritative once loaded; the token's claims cover the gap
  // before it arrives, so the menu is never blank.
  const name = user?.displayName ?? displayName ?? email ?? null
  const address = user?.email ?? email ?? null

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          className="flex items-center gap-sm rounded-full px-sm py-xs transition-colors hover:bg-header-hover"
        >
          <span /*
            The button's own pairing, not a mix of two. This put the primary button's white
            foreground on the lighter `accent` background — 2.98:1, below any readable standard,
            and the initial was the faintest text on the page. `btn-primary-bg` is the background
            that foreground is designed for: 4.47:1. Raising it further means changing the primary
            button colour itself, which is a brand decision rather than a fix.
          */
          className="flex h-8 w-8 items-center justify-center rounded-full bg-btn-primary-bg text-sm font-semibold text-btn-primary-fg">
            {initialsOf(name)}
          </span>
          <span className="hidden max-w-32 truncate text-sm text-header-fg sm:block">{name}</span>
        </button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={8}
          className="min-w-48 rounded-lg border border-card-border bg-card p-xs shadow-lg"
          style={{ zIndex: 'var(--z-dropdown)' }}
        >
          <div className="mb-xs border-b border-card-border px-md py-sm">
            <p className="text-sm font-medium text-fg">{name}</p>
            <p className="text-xs text-fg-muted">{address}</p>
          </div>

          <DropdownMenu.Item
            className="flex cursor-pointer items-center gap-sm rounded-md px-md py-sm text-sm text-fg outline-none transition-colors hover:bg-bg-hover"
            onSelect={() => window.open(DOCS_ROOT, '_blank', 'noopener')}
          >
            <BookOpen size={14} aria-hidden="true" />
            Documentation
          </DropdownMenu.Item>

          <DropdownMenu.Item
            className="flex cursor-pointer items-center gap-sm rounded-md px-md py-sm text-sm text-fg outline-none transition-colors hover:bg-bg-hover"
            onSelect={() => navigate('/downloads/support')}
          >
            <Download size={14} aria-hidden="true" />
            Get the Support app
          </DropdownMenu.Item>

          <DropdownMenu.Item
            className="flex cursor-pointer items-center gap-sm rounded-md px-md py-sm text-sm text-fg outline-none transition-colors hover:bg-bg-hover"
            onSelect={() => void forgetBrowsers()}
          >
            <MonitorX size={14} aria-hidden="true" />
            Forget remembered browsers
          </DropdownMenu.Item>

          <DropdownMenu.Item
            className="flex cursor-pointer items-center gap-sm rounded-md px-md py-sm text-sm text-danger outline-none transition-colors hover:bg-danger-bg"
            onSelect={logout}
          >
            <LogOut size={14} aria-hidden="true" />
            Sign out
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  )
}
